/**
 * Whitelisted use of an element's stored `style_overrides` and of `statusColors`.
 *
 * `style_overrides` is a free-form object in the API, and the renderer used to ignore it. Only these
 * keys are honoured, each a theme token (`token:primary.50`) or a hex colour, resolved like
 * `theme_config` palette values (`theme/canvasThemeConfig.ts`); everything else is dropped:
 *  - `backgroundColor`: card/section tint
 *  - `borderColor`: outline colour
 *  - `accentColor`: a 4px stripe on the left edge
 */
import { resolvePaletteValue } from "../../theme/canvasThemeConfig";

export interface ResolvedStyleOverrides {
  backgroundColor?: string;
  borderColor?: string;
  borderLeft?: string;
}

export function resolveStyleOverrides(
  raw: unknown,
): ResolvedStyleOverrides | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const source = raw as Record<string, unknown>;
  const out: ResolvedStyleOverrides = {};
  const background = resolvePaletteValue(source.backgroundColor);
  if (background) out.backgroundColor = background;
  const border = resolvePaletteValue(source.borderColor);
  if (border) out.borderColor = border;
  const accent = resolvePaletteValue(source.accentColor);
  if (accent) out.borderLeft = `4px solid ${accent}`;
  return Object.keys(out).length > 0 ? out : undefined;
}

export const STATUS_COLOR_NAMES = [
  "primary",
  "neutral",
  "success",
  "warning",
  "danger",
] as const;
export type StatusColor = (typeof STATUS_COLOR_NAMES)[number];

/** A `{value: colour}` map keeping only known colour names, or undefined when none remain. */
export function sanitizeStatusColors(
  raw: unknown,
): Record<string, StatusColor> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const out: Record<string, StatusColor> = {};
  for (const [value, colour] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    if ((STATUS_COLOR_NAMES as readonly string[]).includes(String(colour))) {
      out[value] = colour as StatusColor;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
